import { Component, type ErrorInfo, type ReactNode } from 'react';

/**
 * O limite de erro — a rede embaixo da tela.
 *
 * Em 22/08/2026 um testador clicou numa caixa de perfil e o sistema
 * inteiro virou uma tela marrom vazia. A causa foi banal (ler
 * `e.currentTarget` dentro do atualizador de estado, quando o React já
 * o anulou), mas o **efeito** não foi: sem limite de erro, uma exceção em
 * qualquer manipulador desmonta a árvore inteira. O que fica é o fundo do
 * painel lateral, sem nada em cima — e o testador não tem nem o que
 * relatar, porque a tela não diz nada.
 *
 * Este componente troca isso por três coisas que faltavam:
 *
 *  1. **Dizer que quebrou**, em vez de sumir. Tela vazia é o pior
 *     relatório de erro possível: quem viu não sabe se travou, se
 *     deslogou, ou se apagou alguma coisa.
 *  2. **Mostrar o erro técnico**, recolhido. O testador não precisa
 *     entender; precisa poder copiar.
 *  3. **Não perder o resto do sistema.** "Voltar ao início" recarrega a
 *     rota inicial em vez de exigir que a pessoa descubra sozinha que
 *     recarregar a página resolve.
 *
 * Fica **dentro** de `ProvedorSessao` e **fora** das rotas: assim uma tela
 * quebrada não derruba a sessão, e a pessoa não é jogada para o login com
 * o trabalho pela metade.
 *
 * Classe, e não função, porque `componentDidCatch` não tem equivalente em
 * hook — é a única parte da tela que precisa ser classe.
 */

interface Props { children: ReactNode }
interface Estado { erro: Error | null; pilha: string | null }

export class LimiteDeErro extends Component<Props, Estado> {
  state: Estado = { erro: null, pilha: null };

  static getDerivedStateFromError(erro: Error): Partial<Estado> {
    return { erro };
  }

  componentDidCatch(erro: Error, info: ErrorInfo) {
    /*
     * O console é o único lugar onde a pilha de componentes sobrevive:
     * ela não vem no `Error`, vem no segundo argumento. Quem abrir o
     * console depois de um relato encontra aqui em que componente
     * quebrou, que é a primeira pergunta.
     */
    // eslint-disable-next-line no-console
    console.error('[Labmanager] tela quebrou:', erro, info.componentStack);
    this.setState({ pilha: info.componentStack ?? null });
  }

  render() {
    const { erro, pilha } = this.state;
    if (!erro) return this.props.children;

    const detalhe = [
      `${erro.name}: ${erro.message}`,
      erro.stack ?? '',
      pilha ? `\nComponentes:${pilha}` : '',
    ].join('\n');

    return (
      <div className="tela-quebrou">
        <div className="cartao">
          <h1>Esta tela parou de funcionar</h1>
          <p>
            Alguma coisa quebrou aqui dentro e a tela não conseguiu
            continuar. <strong>Nada do que você já gravou se perdeu</strong> —
            o que estava sendo digitado nesta tela, sim.
          </p>
          <p>
            Use o botão <strong>Relatar</strong> e descreva o que você fez
            antes disso aparecer. É a informação que falta para consertar:
            o erro técnico abaixo diz o que quebrou, não o que você estava
            tentando fazer.
          </p>

          <div className="linha" style={{ marginTop: '1rem' }}>
            <button
              type="button" className="botao"
              onClick={() => { window.location.href = '/'; }}
            >
              Voltar ao início
            </button>
            <button
              type="button" className="botao secundario"
              onClick={() => { window.location.reload(); }}
            >
              Recarregar esta tela
            </button>
          </div>

          <details style={{ marginTop: '1.1rem' }}>
            <summary>Erro técnico</summary>
            <pre className="pilha-erro">{detalhe}</pre>
          </details>
        </div>
      </div>
    );
  }
}
